import type { CustomerDetailsDTO } from "@eu.mdotm.api/sphere";
import { IntegrationEventEventEnum } from "@eu.mdotm.api/sphere";
import { useQuery } from "@tanstack/react-query";
import { useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";

type CustomerExecutionSource = Pick<CustomerDetailsDTO, "integration" | "exportData"> | null | undefined;

/**
 * Whether an integration is an execution transport: enabled, with an EXECUTE hook. The backend dispatches
 * nothing for a disabled integration, so its EXECUTE hook neither sends to execution nor competes with the
 * export service. Structural, so the customer dialog can pass its form state.
 */
export function isExecutionWebhookActive(
	integration: { enabled?: boolean | null; events?: { event?: IntegrationEventEventEnum }[] | null } | null | undefined,
): boolean {
	return (
		integration?.enabled === true &&
		(integration.events?.some((ev) => ev.event === IntegrationEventEventEnum.Execute) ?? false)
	);
}

export function hasExecutionWebhook(customer: CustomerExecutionSource): boolean {
	return isExecutionWebhookActive(customer?.integration);
}

export function hasExportServiceEnabled(customer: CustomerExecutionSource): boolean {
	return customer?.exportData?.enabled ?? false;
}

/**
 * Send-to-execution is available through exactly one transport at a time: the customer's EXECUTE
 * webhook or the export service (Export Sync). The two are mutually exclusive — enforced by the
 * customer dialog (AdvanceSettings), not here — so this is a plain OR.
 */
export function canSendToExecution(customer: CustomerExecutionSource): boolean {
	return hasExecutionWebhook(customer) || hasExportServiceEnabled(customer);
}

/**
 * {@link canSendToExecution} for the signed-in user's customer, for surfaces that have no customer
 * query of their own — the bulk menus. It decides two things there at once:
 *
 * - whether **Send to execution** is offered at all, and
 * - whether bulk **Accept proposal** steps aside for live trading portfolios — with execution
 *   available, those can only be sent to execution, never accepted, which is what the detail page
 *   does when it swaps Accept for Execute.
 *
 * Reads `false` until the customer has loaded.
 */
export function useCanSendToExecution(): boolean {
	const user = useUserValue();
	const { data: customer } = useQuery({
		...userRepository.customer.getQueryConfig(user.customerId ?? ""),
		enabled: Boolean(user.customerId),
	});

	return canSendToExecution(customer);
}
